import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { API } from "@/api";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { GoldImage } from "@/components/GoldImage";

const siteImg = (name) => `${API}/public/site-media/${name}`;

/* Pagina «Chi siamo»: testo ufficiale Tourvoyagers, stile Black Marble & Gold. */
export default function About() {
    const { lang, t } = useLang();
    const a = t.home.aboutPage;

    const Section = ({ title, paras, img, alt, flip, id }) => (
        <section className="grid items-center gap-10 lg:grid-cols-2" data-testid={id}>
            <Reveal className={flip ? "lg:order-2" : ""}>
                <div className="relative overflow-hidden rounded-2xl border border-amber-500/25">
                    <GoldImage src={siteImg(img)} alt={alt} className="aspect-[16/11] w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#080909]/50 to-transparent" />
                </div>
            </Reveal>
            <Reveal delay={0.12} className={flip ? "lg:order-1" : ""}>
                <h2 className="font-display text-2xl text-amber-100 sm:text-3xl lg:text-4xl">{title}</h2>
                <div className="mt-3 h-px w-16 bg-amber-400/70" />
                {paras.map((p, i) => (
                    <p key={i} className="mt-5 text-base leading-relaxed text-stone-300">{p}</p>
                ))}
            </Reveal>
        </section>
    );

    return (
        <div className="marble-bg min-h-screen pt-28 pb-20" data-testid="about-page">
            <Seo lang={lang} title={`${a.title} — TOURVOYAGERS`} description={a.intro1} />

            {/* Hero editoriale */}
            <header className="mx-auto max-w-4xl px-5 text-center sm:px-8">
                <Reveal>
                    <p className="text-[11px] uppercase tracking-[0.3em] font-semibold text-amber-400/90">Tourvoyagers · {a.overline}</p>
                    <h1 className="mt-4 font-display text-4xl leading-tight text-amber-50 sm:text-5xl lg:text-6xl" data-testid="about-title">{a.title}</h1>
                    <p className="mx-auto mt-6 max-w-2xl font-display text-lg italic leading-relaxed text-amber-100/90 sm:text-xl" data-testid="about-intro">{a.intro1}</p>
                    <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-stone-300">{a.intro2}</p>
                    <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-stone-300">{a.intro3}</p>
                </Reveal>
            </header>

            <div className="mx-auto mt-20 max-w-6xl space-y-24 px-5 sm:px-8">
                <Section id="about-idea" title={a.s1t} paras={[a.s1a, a.s1b, a.s1c]} img="dest-roma" alt="Roma, Colosseo" />
                <Section id="about-you" title={a.s2t} paras={[a.s2a, a.s2b]} img="city-venezia" alt="Venezia, Piazza San Marco" flip />
                <Section id="about-hospitality" title={a.s3t} paras={[a.s3a, a.s3b]} img="dest-costiera" alt="Costiera Amalfitana, Positano" />
            </div>

            {/* Chiusura */}
            <section className="mx-auto mt-24 max-w-3xl px-5 text-center sm:px-8" data-testid="about-closing">
                <Reveal>
                    <div className="gold-card rounded-2xl p-10 sm:p-14">
                        <h2 className="font-display text-2xl text-amber-50 sm:text-3xl">{a.s4t}</h2>
                        <p className="mt-5 text-base leading-relaxed text-stone-300">{a.s4a}</p>
                        <p className="mt-6 font-display text-lg italic text-amber-200" data-testid="about-tagline">{a.tagline}</p>
                        <p className="mt-4 text-sm leading-relaxed text-stone-400">{a.s4b}</p>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-3.5">
                            <Link to="/contatti" data-testid="about-cta-contact"
                                className="inline-flex items-center gap-2.5 rounded-full bg-amber-400 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.16em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_24px_rgba(212,175,55,0.4)]">
                                {a.ctaContact} <ArrowUpRight size={15} />
                            </Link>
                            <Link to="/concierge" data-testid="about-cta-concierge"
                                className="inline-flex items-center gap-2.5 rounded-full border border-amber-500/60 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.16em] text-amber-200 transition-colors hover:bg-amber-400/10 hover:border-amber-400">
                                <Sparkles size={14} /> {a.ctaConcierge}
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
